import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';
import SummaryStatsCard from '../components/SummaryStatsCard';

const HOST_API = process.env.REACT_APP_URL;

const formatMoney = (n) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n ?? 0);

const formatPercent = (n) =>
    n != null ? `${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : '—';

const formatPeriodo = (p) => {
    if (!p) return '—';
    const label = new Date(p).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const Morosidad = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const [ufs, setUfs] = useState([]);
    const [ufSel, setUfSel] = useState(null);
    const [periodo, setPeriodo] = useState(new Date());

    const [resumen, setResumen] = useState([]);
    const [detalle, setDetalle] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data ?? []))
            .catch(() => {});
    }, [barrio?.id, administracionId]);

    const buscarMorosidad = useCallback(async () => {
        if (!barrio?.id || !ufSel || !periodo) return;
        setLoading(true);
        setBuscado(true);
        try {
            const params = {
                administracionId,
                barrioId: barrio.id,
                unidadFuncionalId: ufSel,
                periodo: formatDate(periodo),
            };
            const [resumenRes, detalleRes] = await Promise.all([
                axios.get(`${HOST_API}api/Morosidad/GetResumenMorosidad`, { params }),
                axios.get(`${HOST_API}api/Morosidad/GetMorosidad`, { params }),
            ]);
            setResumen(resumenRes.data ?? []);
            setDetalle(detalleRes.data ?? []);
        } catch {
            setResumen([]);
            setDetalle([]);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo obtener la morosidad', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, ufSel, periodo, administracionId]);

    return (
        <>
            <h4>Morosidad</h4>
            <br />
            <Toast ref={toast} />

            <div className="flex align-items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label style={lbl}>{t.UnidadFuncional} *</label>
                    <Dropdown
                        value={ufSel}
                        options={ufs}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfSel(e.value)}
                        placeholder={`Seleccionar ${t.UF}`}
                        filter
                        className="p-inputtext-sm"
                        style={{ width: 220 }}
                    />
                </div>
                <div>
                    <label style={lbl}>Período *</label>
                    <Calendar
                        value={periodo}
                        onChange={(e) => setPeriodo(e.value)}
                        view="month"
                        dateFormat="mm/yy"
                        placeholder="mm/aaaa"
                        className="p-inputtext-sm"
                        style={{ width: 140 }}
                    />
                </div>
                <Button label="Buscar" icon="pi pi-search" className="p-button-sm"
                    onClick={buscarMorosidad} disabled={!ufSel || !periodo} />
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
                </div>
            ) : buscado && (
                <>
                    <h5>Resumen</h5>
                    <div className="mb-4">
                        {(() => {
                            const r = resumen?.[0];
                            const sinDeuda = !r || (r.totalAdeudado ?? 0) <= 0;
                            const titulo = r ? (r.propietario || `${t.UF} ${r.unidadFuncional}`) : 'Resumen';

                            if (sinDeuda) {
                                return (
                                    <SummaryStatsCard icon="pi pi-check-circle" title={titulo} theme="green" style={{ maxWidth: 432 }}>
                                        <p style={{ margin: '0.75rem 0 0', color: '#667085', fontSize: '0.95rem' }}>
                                            La {t.unidadFuncional} no presenta deuda
                                        </p>
                                    </SummaryStatsCard>
                                );
                            }

                            return (
                                <SummaryStatsCard
                                    icon="pi pi-exclamation-triangle"
                                    title={titulo}
                                    theme="red"
                                    style={{ maxWidth: 432 }}
                                    items={[
                                        { label: 'Capital adeudado', value: formatMoney(r.capitalAdeudado) },
                                        { label: 'Intereses', value: formatMoney(r.intereses) },
                                        { label: '% Interés', value: formatPercent(r.porcentajeInteres) },
                                    ]}
                                >
                                    <p style={{ margin: '0.5rem 0 0', color: '#98a2b3', fontSize: '0.78rem' }}>
                                        Período de corte: {formatPeriodo(r.periodoCorte)}
                                    </p>
                                    <div style={{ fontWeight: 700, fontSize: '1.9rem', color: '#dc2626', marginTop: '0.35rem' }}>
                                        {formatMoney(r.totalAdeudado)}
                                    </div>
                                    <p style={{ margin: '0.15rem 0 0', color: '#667085', fontSize: '0.85rem' }}>
                                        {r.periodosAdeudados} {r.periodosAdeudados === 1 ? 'período adeudado' : 'períodos adeudados'}
                                    </p>
                                </SummaryStatsCard>
                            );
                        })()}
                    </div>

                    {detalle.length > 0 && (
                        <>
                            <h5>Detalle por Período</h5>
                            <div className="grid">
                                <div className="col-12">
                                    <DataTable value={detalle} paginator rows={12} rowsPerPageOptions={[12, 24, 50]}
                                        showGridlines responsiveLayout="scroll"
                                        sortField="mes" sortOrder={-1}
                                        className="p-datatable-gridlines bg-white">
                                        <Column field="mes" header="Mes" sortable body={(r) => formatPeriodo(r.mes)} style={{ minWidth: 150 }} />
                                        <Column field="capital" header="Capital" sortable align="right" body={(r) => formatMoney(r.capital)} style={{ minWidth: 120 }} />
                                        <Column field="pago" header="Pago" sortable align="right" body={(r) => formatMoney(r.pago)} style={{ minWidth: 120 }} />
                                        <Column field="saldo" header="Saldo" sortable align="right" body={(r) => formatMoney(r.saldo)} style={{ minWidth: 120 }} />
                                        <Column field="meses" header="Meses" sortable align="center" style={{ minWidth: 90 }} />
                                        <Column field="interes" header="Interés" sortable align="right" body={(r) => formatMoney(r.interes)} style={{ minWidth: 120 }} />
                                        <Column field="totalAdeudado" header="Total Adeudado" sortable align="right" body={(r) => (
                                            <span style={{ fontWeight: 700, color: '#f04438' }}>{formatMoney(r.totalAdeudado)}</span>
                                        )} style={{ minWidth: 140 }} />
                                    </DataTable>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default Morosidad;
